import { createSlice } from '@reduxjs/toolkit';
import { subMonths } from 'date-fns';
import {
  alteraCorSistema,
  coresPrincipais,
  coresPlanoA,
  coresSecundarias,
} from '@utils';
import { initialState } from './initialState';
import { resetAllStores, resetPlanScopedStores } from '../sharedActions';
import {
  getDataCorte,
  getListagemRequerimentos,
  getPersonalizacao,
  getMenuAtendimentoVirtual,
  getTelasDisponiveis,
  setPersonalizacao,
} from './thunks';

const configsSlice = createSlice({
  name: 'configs',
  initialState,
  reducers: {
    resetConfigs: (state) => {
      state.dataCorte = initialState.dataCorte;
      state.listaRequerimentos = initialState.listaRequerimentos;
      state.personalizacao = initialState.personalizacao;
      state.telasDisponiveis = initialState.telasDisponiveis;
      state.tema = initialState.tema;
      state.recuperacaoSenha = initialState.recuperacaoSenha;
      state.temporizador = initialState.temporizador;
    },
    forceEndLoading: (state) => {
      state.personalizacao.loading = false;
    },
    setTemaPorPlano: (state, { payload: { plano } }) => {
      let cores = coresSecundarias;

      if (Number(plano) === 1) {
        cores = coresPrincipais;
      } else if (Number(plano) === 3) {
        cores = coresPlanoA;
      }

      alteraCorSistema(cores.geral, cores.hover, cores.focus);

      state.tema = {
        codCorPrincipal: cores.geral,
        nomeCorPrincipal: cores.nome,
        codCorHover: cores.hover,
        codCorFocus: cores.focus,
        codCorLaranja: '#e87400',
      };
    },
    setRecuperacaoSenhaDados: (state, { payload }) => {
      state.recuperacaoSenha = {
        ...state.recuperacaoSenha,
        email: payload.email,
        telefone: payload.telefone,
        matricula: payload.matricula,
        token: payload.token,
        etapa: payload.etapa,
      };
    },
    setTemporizador: (state, { payload }) => {
      state.temporizador = {
        ...state.temporizador,
        tempo: payload.tempo,
        situacao: payload.situacao,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetAllStores, () => initialState);
    builder.addCase(resetPlanScopedStores, (state) => {
      state.menuAtendimentoVirtual = initialState.menuAtendimentoVirtual;
      state.telasDisponiveis = initialState.telasDisponiveis;
      state.banners = initialState.banners;
      state.personalizacao = initialState.personalizacao;
      state.listaRequerimentos = initialState.listaRequerimentos;
      state.dataCorte = initialState.dataCorte;
    });

    builder.addCase(getMenuAtendimentoVirtual.pending, (state) => {
      state.menuAtendimentoVirtual.loading = true;
      state.menuAtendimentoVirtual.error = false;
    });

    builder.addCase(
      getMenuAtendimentoVirtual.fulfilled,
      (state, { payload }) => {
        state.menuAtendimentoVirtual = {
          data: payload.itensMenuAtendimentoVirtual || [],
          loading: false,
          error: false,
        };
      }
    );

    builder.addCase(getMenuAtendimentoVirtual.rejected, (state) => {
      state.menuAtendimentoVirtual = {
        data: [],
        loading: false,
        error: true,
      };
    });

    builder.addCase(getTelasDisponiveis.pending, (state) => {
      state.telasDisponiveis.loading = true;
    });

    builder.addCase(getTelasDisponiveis.fulfilled, (state, { payload }) => {
      let telasDisponiveis = initialState.telasDisponiveis.data;

      payload.forEach((key) => {
        telasDisponiveis = {
          ...telasDisponiveis,
          [key]: true,
        };
      });

      state.telasDisponiveis = {
        data: telasDisponiveis,
        loading: false,
        error: false,
      };
    });

    builder.addCase(getTelasDisponiveis.rejected, (state) => {
      state.telasDisponiveis = {
        data: initialState.telasDisponiveis.data,
        loading: false,
        error: false,
      };
    });

    builder.addCase(getPersonalizacao.fulfilled, (state, { payload }) => {
      state.personalizacao.data = payload;
      state.personalizacao.loading = false;
      state.personalizacao.error = false;
    });

    builder.addCase(getPersonalizacao.rejected, (state) => {
      state.personalizacao = {
        data: initialState.personalizacao.data,
        loading: false,
        error: true,
      };
    });

    builder.addCase(setPersonalizacao.fulfilled, (state, { payload }) => {
      state.personalizacao.data = payload;
      state.personalizacao.loading = false;
      state.personalizacao.error = false;
    });

    builder.addCase(setPersonalizacao.rejected, (state) => {
      state.personalizacao.loading = false;
    });

    builder.addCase(getListagemRequerimentos.pending, (state) => {
      state.listaRequerimentos.loading = true;
    });

    builder.addCase(
      getListagemRequerimentos.fulfilled,
      (state, { payload }) => {
        state.listaRequerimentos = {
          data: payload,
          loading: false,
          error: false,
        };
      }
    );

    builder.addCase(getListagemRequerimentos.rejected, (state) => {
      state.listaRequerimentos = { data: [], loading: false, error: true };
    });

    builder.addCase(getDataCorte.pending, (state) => {
      state.dataCorte.loading = true;
    });

    builder.addCase(getDataCorte.fulfilled, (state, { payload }) => {
      state.dataCorte = {
        data: {
          corteExtrato: payload.corteExtrato,
          corteContracheque: payload.corteContracheque,
        },
        loading: false,
        error: false,
      };
    });

    builder.addCase(getDataCorte.rejected, (state) => {
      const lastDate = subMonths(new Date(), 1);
      const lastMonth = lastDate.getMonth() + 1;
      const lastYear = lastDate.getFullYear();

      state.dataCorte = {
        data: {
          corteExtrato: [lastMonth.toString(), lastYear.toString()],
          corteContracheque: [lastMonth.toString(), lastYear.toString()],
        },
        loading: false,
        error: true,
      };
    });
  },
});

export const configs = configsSlice.reducer;
export const {
  resetConfigs,
  forceEndLoading,
  setTemaPorPlano,
  setRecuperacaoSenhaDados,
  setTemporizador,
} = configsSlice.actions;
